import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Play } from "@phosphor-icons/react";
import { reelsApi, toAbsolute } from "@/lib/apiClient";
import { ReelUnavailableState } from "@/components/ReelManagement";

export default function ReelDeepLink() {
    const { reelId } = useParams();
    const [loading, setLoading] = useState(true);
    const [reel, setReel] = useState(null);
    const [errorState, setErrorState] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        reelsApi.get(reelId)
            .then((item) => {
                if (!mounted) return;
                setReel(item);
                setErrorState(null);
            })
            .catch((error) => {
                if (!mounted) return;
                const status = error?.response?.status;
                if (status === 404) {
                    setErrorState({
                        title: "Reel no longer available.",
                        description: "This Reel has been deleted or removed, so the old link can’t be played anymore.",
                    });
                } else {
                    setErrorState({
                        title: "This reel can’t be opened right now.",
                        description: "Try opening another Reel from the Travel Feed.",
                    });
                }
                setReel(null);
            })
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, [reelId]);

    if (loading) return <div className="min-h-screen grid place-items-center text-white/60">Loading…</div>;
    if (errorState) {
        return (
            <div className="min-h-screen grain px-6 py-16 grid place-items-center">
                <div className="w-full max-w-3xl">
                    <ReelUnavailableState title={errorState.title} description={errorState.description} onBack={() => { window.location.href = "/app?tab=feed"; }} />
                </div>
            </div>
        );
    }
    if (!reel) return null;

    return (
        <div className="min-h-screen grain px-6 md:px-10 py-10" data-testid="reel-deep-link-page">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.7fr_0.55fr] gap-8 items-start">
                <div className="rounded-[32px] overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <div className="relative aspect-[9/16] bg-black max-h-[80vh]">
                        <video src={toAbsolute(reel.media_url)} poster={toAbsolute(reel.poster_url) || undefined} controls autoPlay playsInline className="w-full h-full object-cover" data-testid="reel-deep-link-video" />
                        <div className="absolute right-5 bottom-5 w-14 h-14 rounded-full grid place-items-center" style={{ background: "rgba(5,10,17,0.45)", border: "1px solid rgba(255,255,255,0.12)" }}>
                            <Play size={18} weight="fill" />
                        </div>
                    </div>
                </div>
                <div className="pt-4 lg:pt-10">
                    <div className="overline mb-2">/ Reel link</div>
                    <div className="font-serif text-5xl text-white leading-[0.95] mb-4" data-testid="reel-deep-link-caption">{reel.caption}</div>
                    <div className="text-white/65 text-sm mb-6">@{reel.user_handle} · {reel.recommendation_reason}</div>
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6" style={{ background: "rgba(255,255,255,0.06)", color: "white" }} data-testid="reel-deep-link-location">
                        <MapPin size={14} color="#D4AF37" /> {reel.location || reel.destination || reel.country || "Passport"}
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed mb-8">Open the full Passport Reels viewer to like, save, comment, and keep exploring this destination.</p>
                    <div className="flex flex-wrap gap-3">
                        <Link to={`/app?tab=reels&reel=${reel.id}`} className="btn-gold text-sm !py-2.5 !px-5" data-testid="reel-deep-link-open">Open in Passport Reels</Link>
                        <Link to="/app?tab=feed" className="btn-ghost text-sm !py-2.5 !px-5" data-testid="reel-deep-link-back">Back to Travel Feed</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}